import { FC, ReactElement, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { Button, ButtonGroup, Stack, Flex, Box, Text } from '@chakra-ui/react';
import { CbSelect } from '../CbSelect';
export interface ScheduleTimeButtonGroupProps {
  disabled?: boolean;
  hour: number;
  minutes: number;
  editEndTime?: string;
  startDate?: string;
  endDate?: string;
  timezone: string;
  setHour: (hour: string | {}) => void;
  setMinutes: (minute: string | {}) => void;
}
export const ScheduleTimeButtonGroup: FC<ScheduleTimeButtonGroupProps> = ({
  disabled,
  hour,
  minutes,
  setHour,
  setMinutes,
  timezone
}): ReactElement => {
  const [isPM, setIsPM] = useState(hour >= 12);
  let hourValue = hour - (isPM ? 12 : 0);
  // 0:00 + AM is 12:00
  if (hourValue === 0) {
    hourValue = 12;
  }
  // 0:00 + PM is also 12:00
  if (hourValue === -12) {
    hourValue = 12;
  }

  /*
    There are some discrepancies afoot with calculated dates that we are working
    to resolve one by one, but in the meantime, this component is hard-coded to only
    offer minute selection of 0, 15, 30 or 45.  If this component receives a different
    value, re-set it to 0 and report to Sentry so we can fix the root cause.
  */
  useEffect(() => {
    const validValues = [...btnTimeMinutes, '0'];
    if (validValues.includes(minutes.toString())) return;
    setMinutes('00');
    Sentry.captureMessage('ScheduleTimeButtonGroup received minutes value that was not from expected values', {
      extra: {
        url: window.location.toString()
      }
    });
  }, []);
  const buttonStyle = {
    _hover: {
      bg: '',
      border: ''
    },
    _active: {
      bg: '',
      border: ''
    },
    _focus: {
      bg: '',
      border: ''
    },
    borderRadius: 50,
    height: 8,
    isDisabled: disabled
  };
  return <Flex marginTop={5} alignItems="center" minWidth="318px" data-sentry-element="Flex" data-sentry-component="ScheduleTimeButtonGroup" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
      <ButtonGroup data-sentry-element="ButtonGroup" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
        <CbSelect data-testid="schedule-time-hour" isDisabled={disabled} height={8} value={hourValue.toString()} options={btnTimeHours} minWidth="20px" onChange={e => {
        setHour(parseInt(e as string, 10) + (isPM ? 12 : 0));
      }} data-sentry-element="CbSelect" data-sentry-source-file="ScheduleTimeButtonGroup.tsx" />
        <CbSelect data-testid="schedule-time-minute" isDisabled={disabled} height={8} value={minutes ? `${minutes}` : '00'} options={btnTimeMinutes} onChange={e => {
        setMinutes(parseInt(e as string, 10));
      }} data-sentry-element="CbSelect" data-sentry-source-file="ScheduleTimeButtonGroup.tsx" />
        <Box borderRadius={50} borderWidth={'1px'} height={8} borderColor="cloud.300" data-sentry-element="Box" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
          <Stack direction="row" spacing={0} data-sentry-element="Stack" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
            <Button {...buttonStyle} color={!isPM ? 'white' : 'text.primary'} margin="-1px" backgroundColor={!isPM ? 'luigisPants.500' : 'transparent'} onClick={() => {
            setIsPM(false);
            setHour(hour % 12);
          }} data-sentry-element="Button" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
              AM
            </Button>
            <Button {...buttonStyle} color={isPM ? 'white' : 'text.primary'} margin="-1px" backgroundColor={isPM ? 'luigisPants.500' : 'transparent'} onClick={() => {
            setIsPM(true);
            setHour(hour % 12 + 12);
          }} data-sentry-element="Button" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
              PM
            </Button>
          </Stack>
        </Box>
        <Text marginLeft={2} marginTop={1} whiteSpace="nowrap" fontSize={14} data-sentry-element="Text" data-sentry-source-file="ScheduleTimeButtonGroup.tsx">
          {timezone}
        </Text>
      </ButtonGroup>
    </Flex>;
};
const btnTimeHours = ['9', '10', '11', '12', '1', '2', '3', '4', '5', '6', '7', '8'];
const btnTimeMinutes = ['00', '15', '30', '45'];